// store/slices/paginationSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { KnowledgeType } from "../util/KnowledgeType";
import { SideCardType } from "../util/QuestionStatus";
import { fetchBrainList } from "./brain.slice";
import { fetchChatHistory } from "./chat.slice";
import { AppThunk } from "./common/store";
import { fetchContext } from "./context.slice";
import { fetchConversations } from "./conversations.slice";
import { fetchImages } from "./images.slice";
import { fetchIntents } from "./intent.slice";
import { fetchSopKnowledge } from "./sop.slice";

interface PaginationState {
  currentPage: number;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
  totalPages: number;
  totalCount: number;
}

const initialState: PaginationState = {
  currentPage: 1,
  nextPageUrl: null,
  prevPageUrl: null,
  totalPages: 1,
  totalCount: 0,
};

export function getPageNumber(
  url: string,
  base: string = window.location.origin
): number {
  try {
    const u = new URL(url, base);
    const p = parseInt(u.searchParams.get("page") ?? "", 10);
    return Number.isNaN(p) ? 1 : p;
  } catch {
    return 1;
  }
}

export const paginateCurrentSection =
  (page: number): AppThunk =>
  (dispatch, getState) => {
    const { ui, brain } = getState();
    dispatch(setPagination({ currentPage: page }));

    switch (ui.statusClicked) {
      case SideCardType.Context:
        dispatch(fetchContext({ page }));
        break;
      case SideCardType.Brain:
        if (brain.knowledgeType === KnowledgeType.SOP)
          dispatch(fetchSopKnowledge({ page }));
        else if (brain.knowledgeType === KnowledgeType.INTENT)
          dispatch(fetchIntents({ page }));
        else dispatch(fetchBrainList({ page }));
        break;
      case SideCardType.Images:
        dispatch(fetchImages({ page }));
        break;
      case SideCardType.Chat:
        dispatch(fetchChatHistory({ page }));
        break;
      default:
        dispatch(fetchConversations({ page }));
        break;
    }
  };

const paginationSlice = createSlice({
  name: "pagination",
  initialState,
  reducers: {
    setPagination(state, action: PayloadAction<Partial<PaginationState>>) {
      return { ...state, ...action.payload };
    },
    resetPagination() {
      return initialState;
    },
  },
});

export const { setPagination, resetPagination } = paginationSlice.actions;
export default paginationSlice.reducer;
